import type * as React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartTooltip, useChartTooltipTrigger, useIsTouchOnly } from './ChartTooltip';

/** Recharts hands a <Bar> click the bar entry, whose `payload` is the row. */
function rowOf<T>(entry: unknown): T | undefined {
  const e = entry as { payload?: T } | undefined;
  return e?.payload ?? (entry as T | undefined);
}

/**
 * One bar chart, the RatingDistribution pattern (components/RatingDistribution.tsx): the house
 * tooltip, the hover outline on a clickable bar, the permanent outline and no tooltip on touch
 * (there the tap opens the modal, which IS the detail view).
 */
export function Bars<T extends Record<string, unknown>>({
  data,
  categoryKey,
  categoryLabel,
  series,
  vertical = false,
  height = 240,
  onPick,
  testId,
  valueFormatter,
  emptyMessage = 'Nothing in selected period',
}: {
  data: T[];
  categoryKey: keyof T & string;
  categoryLabel?: (v: string) => string;
  series: Array<{ key: keyof T & string; name: string; color: string }>;
  vertical?: boolean;
  height?: number;
  onPick?: (row: T) => void;
  testId: string;
  emptyMessage?: string;
  valueFormatter?: (value: unknown, name: string | undefined) => React.ReactNode;
}): React.ReactElement {
  const tooltipTrigger = useChartTooltipTrigger();
  const isTouchOnly = useIsTouchOnly();
  if (data.length === 0 || data.every((r) => series.every((s) => !r[s.key]))) {
    return (
      <div data-testid={`${testId}-empty`} className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        {emptyMessage}
      </div>
    );
  }
  const label = (v: unknown) => (categoryLabel ? categoryLabel(String(v)) : String(v));
  const byCategory = new Map(data.map((r) => [String(r[categoryKey]), r]));
  return (
    <div data-testid={testId} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout={vertical ? 'vertical' : 'horizontal'} margin={{ top: 8, right: 24, left: vertical ? 8 : 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          {/* Direct children, never a fragment: Recharts 2 reads its axes off the chart's own children
              and does not look inside `<>…</>` — wrapped, both axes were dropped, the charts drew no
              labels and a vertical chart kept one bar (dashboard screenshot 2026-09-29). */}
          {vertical
            ? <XAxis type="number" allowDecimals={false} />
            : <XAxis dataKey={categoryKey} type="category" tickFormatter={label} interval={0} tick={{ fontSize: 11 }} />}
          {vertical
            ? <YAxis dataKey={categoryKey} type="category" width={190} tickFormatter={label} tick={{ fontSize: 11 }} />
            : <YAxis allowDecimals={false} />}
          {!(isTouchOnly && onPick) && (
            <Tooltip
              {...tooltipTrigger}
              cursor={false}
              content={
                <ChartTooltip
                  labelFormatter={label}
                  valueFormatter={valueFormatter}
                />
              }
            />
          )}
          {series.length > 1 && <Legend />}
          {series.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              fill={s.color}
              isAnimationActive={false}
              cursor={onPick ? 'pointer' : 'default'}
              activeBar={onPick ? { stroke: 'hsl(var(--foreground))', strokeWidth: 1.5 } : false}
              stroke={onPick && isTouchOnly ? 'hsl(var(--foreground))' : undefined}
              strokeWidth={onPick && isTouchOnly ? 1 : 0}
              strokeOpacity={onPick && isTouchOnly ? 0.6 : 1}
              onClick={
                onPick
                  ? (entry: unknown) => {
                      const row = rowOf<T>(entry);
                      const hit = row ? byCategory.get(String(row[categoryKey])) : undefined;
                      if (hit) onPick(hit);
                    }
                  : undefined
              }
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
